import { classNames, cn } from '@commenda/utils';
// import removed: SortingState not used here
import type { Table as TanTable } from '@tanstack/react-table';
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ContextMenuMenuProps } from '../ContextMenu';
import { Table, TableBody as TBody, TableHeader as THead } from '../Table';
import { DEFAULT_GROUP_MAX_HEIGHT } from './constants';
import { useExpandColumn } from './hooks/useExpandColumn';
import { useGrouping } from './hooks/useGrouping';
import { useRowSelection } from './hooks/useRowSelection';
import { useSorting } from './hooks/useSorting';
import { useTable } from './hooks/useTable';
import { RowSelectActionMenu } from './internal/RowSelectActionMenu';
import { TableEmptyState } from './internal/TableEmptyState';
import { TableError } from './internal/TableError';
import { TableGroupRow } from './internal/TableGroupRow';
import { TableHeader } from './internal/TableHeader';
import { TableLoadingState } from './internal/TableLoadingState';
import { TableRow } from './internal/TableRow';
import { TableSummary } from './internal/TableSummary';
import { TableToolbar } from './internal/TableToolbar';
import { UnifiedTablePagination } from './internal/UnifiedTablePagination';
import {
  ColumnGroup,
  ColumnResizeControl,
  CustomColumnDef,
  EmptyStateConfigProps,
  GroupingConfig,
  TableProps,
  TreeNode,
} from './types';
import {
  getLeftStickyTotalWidth,
  getRightStickyTotalWidth,
  measureColumnContentCap,
} from './utils';
import { applyFilters, hasActiveFilters } from './utils/filters';
import { filterBySearch } from './utils/search';

// Freezes rows during a column resize (data unchanged) so widths move via CSS
// vars; re-renders normally otherwise. Same component type always — no remount.
// Requires BOTH prev and next to be resizing so the drag-START transition still
// renders once — otherwise the resize handle can freeze at opacity-0 (e.g. a
// second drag from the same spot, where the cursor never re-enters to re-hover).
const MemoizedTableRow = memo(
  TableRow,
  (prev, next) =>
    !!prev.isResizingColumn &&
    !!next.isResizingColumn &&
    prev.table.options.data === next.table.options.data
) as typeof TableRow;

/** Enforces a single sticky group per side; returns a copy with extras unstuck. */
function normalizeStickyColumnGroups<TData, TValue>(
  groups: ColumnGroup<TData, TValue>[]
): ColumnGroup<TData, TValue>[] {
  const groupsAfterReordering = groups.map((g) => ({
    ...g,
    columns: [...g.columns],
  }));

  // Enforce only one sticky group per side (left/right)
  let leftTaken = false;
  let rightTaken = false;

  return groupsAfterReordering.map((g) => {
    if (g.sticky === 'left') {
      if (leftTaken) return { ...g, sticky: undefined };
      leftTaken = true;
      return g;
    }
    if (g.sticky === 'right') {
      if (rightTaken) return { ...g, sticky: undefined };
      rightTaken = true;
      return g;
    }
    return g;
  });
}

export function DataTable<TData, TValue>({
  columnGroups,
  data,
  paginationConfig = {
    isEnabled: false,
    pageSize: 10,
    type: 'PAGE',
  },
  searchConfig = {
    isEnabled: false,
    placeholder: '',
    columnsToEnableSearchFor: [],
    searchValue: '',
  },
  columnSelectorConfig = {
    isEnabled: false,
    placeholder: 'Customize',
    searchText: 'Search columns',
    notFoundText: 'No columns found',
    selectAllText: 'Show all columns',
  },
  tableViewConfig = {
    isEnabled: false,
    defaultView: 'regular',
  },
  rowSelectionConfig = {
    isEnabled: false,
    selectionMode: 'multiple',
    onSelectionChange: () => {},
    initialSelectedRows: [],
    // Default assumes rows have an `id` field; callers can override
    getRowId: (row: unknown) => (row as { id: string }).id,
    rowSelectActionMenu: () => null,
    isRowSelected: undefined,
    shouldShowRowSelectActionMenu: true,
  },
  tableContainerClassName = '',
  rowClassName = () => '',
  stickyRowBgClassName,
  headerClassName = '',
  tableToolbarClassName = '',
  tableHeadClassName = '',
  tableClassName = '',
  tableCellClassName = '',
  tableRowClassName = '',
  tableWrapperClassName = '',
  isLoading = false,
  onRowClick,
  onRowMouseEnter,
  onRowMouseLeave,
  isRowHovered,
  rowContextMenu,
  rowDataAttributes,
  emptyStateConfig = {
    title: 'No results found',
    description: 'Try adjusting your filters or search terms',
    action: undefined,
  },
  errorConfig,
  tableSortConfig,
  customActions,
  renderToolbar,
  groupingConfig,
  // new
  expandableRowConfig,
  columnResizeConfig = { isEnabled: false },
  addRowConfig,
  loadingConfig = {
    loadingRowsCount: 10,
    showPagination: true,
  },
  filterConfig,
  summaryConfig,
  sortToolbar = false,
}: TableProps<TData, TValue>) {
  const [searchValue, setSearchValue] = useState('');

  // Local reorderable copy of incoming column groups (excludes utility columns we prepend)
  const [reorderableGroups, setReorderableGroups] = useState<
    ColumnGroup<TreeNode<TData>, TValue>[]
  >(() => {
    return normalizeStickyColumnGroups(
      columnGroups as ColumnGroup<TreeNode<TData>, TValue>[]
    );
  });
  const prevDataRef = useRef<TreeNode<TData>[]>(data as TreeNode<TData>[]);
  // Track when search was just applied directly to avoid duplicate work in filter effect
  const lastAppliedSearchRef = useRef<string>('');
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);

  // fit-content resize caps, measured lazily at drag-start (read by useTable's clamp).
  const fitContentCapsRef = useRef<Record<string, number>>({});
  const handleColumnResizeStart = useCallback(
    (columnId: string, configuredSize?: number) => {
      const cap = measureColumnContentCap(tableRef.current, columnId);
      if (cap == null) return;
      // Floor at the configured width so a column wider than its content doesn't snap on grab.
      fitContentCapsRef.current[columnId] = Math.max(cap, configuredSize ?? 0);
    },
    []
  );

  // Normalize stickiness when incoming columnGroups prop changes
  useEffect(() => {
    const normalized = normalizeStickyColumnGroups(
      columnGroups as ColumnGroup<TreeNode<TData>, TValue>[]
    );
    setReorderableGroups(normalized as ColumnGroup<TreeNode<TData>, TValue>[]);
  }, [columnGroups]);

  // State to store filtered data that will be passed to the table
  // This allows us to filter the data based on search input while preserving the original data
  const [filteredData, setFilteredData] = useState<TreeNode<TData>[]>(
    data as TreeNode<TData>[]
  );

  const [rowHeightView, setRowHeightView] = useState(
    tableViewConfig.defaultView
  );

  // Sticky scrollbar state
  const [showStickyScrollbar, setShowStickyScrollbar] = useState(false);
  const [fullTableWidth, setFullTableWidth] = useState(0);
  const [containerLeft, setContainerLeft] = useState(0);
  const [containerRight, setContainerRight] = useState(0);
  // Track container dimensions to force re-render when layout changes (e.g., navbar expansion)
  const [containerWidth, setContainerWidth] = useState(0);
  const stickyScrollbarRef = useRef<HTMLDivElement>(null);

  // Edge-shadow state: indicates content is hidden under sticky columns or beyond viewport
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [tableHovered, setTableHovered] = useState(false);

  // Grouping
  const { groupedData, expandedGroups, toggleGroup } = useGrouping(
    filteredData as TreeNode<TData>[],
    groupingConfig as GroupingConfig<TreeNode<TData>> | undefined
  );

  const {
    selectedRows,
    unselectAllRows,
    isRowSelectedFn,
    toggleRowById,
    toggleAll,
    getRowIdInternal,
  } = useRowSelection(filteredData, rowSelectionConfig);

  // Expandable rows
  const {
    column: expanderColumn,
    expandedIdSet,
    toggleExpandById,
  } = useExpandColumn<TreeNode<TData>>({
    getRowId: expandableRowConfig?.getRowId as (r: TreeNode<TData>) => string,
    isRowExpandable: expandableRowConfig?.isRowExpandable as
      | ((r: TreeNode<TData>) => boolean)
      | undefined,
    initiallyExpandedIds: expandableRowConfig?.initiallyExpandedIds,
    expandedIds: expandableRowConfig?.expandedIds,
    onExpandedChange: expandableRowConfig?.onExpandedChange,
    sticky: expandableRowConfig?.expandColumn?.sticky,
    width: expandableRowConfig?.expandColumn?.width,
    ariaLabelToggle: expandableRowConfig?.expandColumn?.ariaLabelToggle,
    enabled: !!expandableRowConfig?.isEnabled,
    hasRowSelection: !!rowSelectionConfig?.isEnabled,
  });

  // Compose final column groups (no dedicated checkbox column; selection UI is embedded in first column)
  let finalColumnGroups: ColumnGroup<TreeNode<TData>, TValue>[] =
    reorderableGroups as ColumnGroup<TreeNode<TData>, TValue>[];
  if (expanderColumn) {
    finalColumnGroups = [
      expanderColumn as ColumnGroup<TreeNode<TData>, TValue>,
      ...finalColumnGroups,
    ];
  }

  const { table, setGlobalFilter, handlePageSizeChange, currentPageSize } =
    useTable({
      data: filteredData,
      groups: finalColumnGroups,
      paginationConfig,
      searchConfig,
      rowSelectionConfig,
      sortingConfig: tableSortConfig ?? {},
      defaultVisibleColumns: columnSelectorConfig.defaultVisibleColumns,
      columnResizeConfig,
      fitContentCapsRef,
    });

  const flattenedColumns = finalColumnGroups.flatMap((group) => group.columns);

  // Publish `--col-<id>-size` vars so a resize updates widths via CSS, not a
  // per-frame re-render. Recomputes only when sizing state changes.
  const resizeColumnSizing = table.getState().columnSizing;
  const resizeColumnSizingInfo = table.getState().columnSizingInfo;
  const columnSizeVars = useMemo<Record<string, string> | undefined>(() => {
    if (!columnResizeConfig?.isEnabled) return undefined;
    const vars: Record<string, string> = {};
    table.getFlatHeaders().forEach((header) => {
      vars[`--col-${header.column.id}-size`] = `${header.column.getSize()}px`;
    });
    return vars;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    columnResizeConfig?.isEnabled,
    resizeColumnSizing,
    resizeColumnSizingInfo,
  ]);

  // Id of the column being actively resized (or false), mirrored to a ref for
  // the hover setter to read without churning its identity.
  const activeResizeColumnId = resizeColumnSizingInfo.isResizingColumn;
  const activeResizeColumnIdRef = useRef(activeResizeColumnId);
  activeResizeColumnIdRef.current = activeResizeColumnId;

  // Hovered column edge, shared across all of a column's cells so the handle
  // lights up as one continuous top-to-bottom line.
  const [hoveredResizeColumnId, setHoveredResizeColumnId] = useState<
    string | null
  >(null);
  const hoverClearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const setResizeHover = useCallback((columnId: string | null) => {
    if (hoverClearTimerRef.current) {
      clearTimeout(hoverClearTimerRef.current);
      hoverClearTimerRef.current = null;
    }
    if (columnId !== null) {
      setHoveredResizeColumnId(columnId);
      return;
    }
    // Keep the line lit for the whole drag (the cursor leaves the thin handle
    // as the column widens); resize-end clears it.
    if (activeResizeColumnIdRef.current) return;
    // Defer clearing so moving between a column's stacked handles (across the
    // 1px cell seams) doesn't flicker the highlight; the next enter cancels it.
    hoverClearTimerRef.current = setTimeout(
      () => setHoveredResizeColumnId(null),
      0
    );
  }, []);
  useEffect(
    () => () => {
      if (hoverClearTimerRef.current) clearTimeout(hoverClearTimerRef.current);
    },
    []
  );

  // Per-column resize controls so body cells can host the same drag handle as
  // the header — this lets the user grab a column edge from any row, not just
  // the header. Reuses the header's TanStack resize handler and fit-content cap.
  const columnResizeControls = useMemo<
    Record<string, ColumnResizeControl> | undefined
  >(() => {
    if (!columnResizeConfig?.isEnabled) return undefined;
    const controls: Record<string, ColumnResizeControl> = {};
    table.getFlatHeaders().forEach((header) => {
      if (!header.column.getCanResize()) return;
      const columnDef = header.column.columnDef as CustomColumnDef<
        TreeNode<TData>,
        TValue
      >;
      const columnId = header.column.id;
      // Derive from the same resize info the row-freeze uses, so the header
      // stays lit for the full drag even when it started from a body row.
      const isResizing = activeResizeColumnId === columnId;
      controls[columnId] = {
        onResizeStart: (event) => {
          // Mirror the header: measure the fit-content cap unless opted out.
          if (columnDef.maxWidth !== 'none') {
            handleColumnResizeStart(columnId, header.column.columnDef.size);
          }
          // Pin the hover to this column so the whole line stays lit while the
          // cursor drags away from the handle.
          setHoveredResizeColumnId(columnId);
          header.getResizeHandler()(event);
        },
        isActive: isResizing || hoveredResizeColumnId === columnId,
        onHoverStart: () => setResizeHover(columnId),
        onHoverEnd: () => setResizeHover(null),
      };
    });
    return controls;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    columnResizeConfig?.isEnabled,
    resizeColumnSizing,
    resizeColumnSizingInfo,
    activeResizeColumnId,
    hoveredResizeColumnId,
    setResizeHover,
  ]);

  const isResizingColumn = !!(
    columnResizeConfig?.isEnabled && resizeColumnSizingInfo.isResizingColumn
  );

  // Report the final widths once the drag ends, rather than on every frame, so
  // consumers can persist them without doing work mid-resize.
  const onColumnSizingChangeRef = useRef(
    columnResizeConfig?.onColumnSizingChange
  );
  onColumnSizingChangeRef.current = columnResizeConfig?.onColumnSizingChange;
  const columnSizingRef = useRef(resizeColumnSizing);
  columnSizingRef.current = resizeColumnSizing;
  const wasResizingColumnRef = useRef(false);
  useEffect(() => {
    if (wasResizingColumnRef.current && !isResizingColumn) {
      onColumnSizingChangeRef.current?.(columnSizingRef.current);
      // Drop the hover pinned at drag-start; the cursor is no longer on the edge.
      setHoveredResizeColumnId(null);
    }
    wasResizingColumnRef.current = isResizingColumn;
  }, [isResizingColumn]);

  const { sortRowsForCurrentSorting, sortReactTableRowsForCurrentSorting } =
    useSorting(
      table as unknown as TanTable<TData>,
      finalColumnGroups as unknown as ColumnGroup<TData, TValue>[],
      tableSortConfig?.isClientSortingDisabled
    );

  /**
   * Handles search functionality for the DataTable.
   * This function:
   * 1. Updates the search value state
   * 2. Sets the global filter for the table
   * 3. Filters the data passed into the table as well
   *
   * @param value - The search value
   * @param isFromEffect - Flag to indicate if the call is from the useEffect to prevent infinite loops
   */
  const handleSearch = useCallback(
    (value: string, isFromEffect = false) => {
      if (!isFromEffect) {
        setSearchValue(value);
      }

      // When isClientSearchDisabled is true, skip all client-side filtering
      // Just call onSearch and let the parent handle filtering via data prop
      if (searchConfig.isClientSearchDisabled) {
        if (!isFromEffect && searchConfig.onSearch) {
          searchConfig.onSearch(value);
        }
        return;
      }

      // If search value is empty, clear global filter and let filter effect handle the rest
      if (value.trim() === '') {
        setGlobalFilter('');
        // Reset the ref so filter effect can handle clearing
        if (!isFromEffect) {
          lastAppliedSearchRef.current = '';
          if (searchConfig.onSearch) {
            searchConfig.onSearch(value);
          }
        }
        return;
      }

      // When called directly (user typing), apply search immediately for instant feedback
      // The filter effect will also run to ensure consistency with active filters
      if (!isFromEffect) {
        // Check if there are active filters - if so, we need to apply filters first
        const activeFilters = filterConfig?.activeFilters || {};
        const hasActiveFilters = Object.keys(activeFilters).some(
          (key) =>
            (Array.isArray(activeFilters[key]) &&
              activeFilters[key].length > 0) ||
            (typeof activeFilters[key] === 'string' &&
              activeFilters[key] !== '')
        );

        if (hasActiveFilters) {
          // If there are active filters, apply filters first, then search
          const filtered = applyFilters(
            data as TreeNode<TData>[],
            filterConfig
          );
          const searched = filterBySearch(
            filtered,
            value,
            searchConfig.columnsToEnableSearchFor,
            searchConfig.onFilterData as
              | ((d: TreeNode<TData>[], v: string) => TreeNode<TData>[])
              | undefined
          ) as TreeNode<TData>[];
          if (searched === filtered) {
            setFilteredData(filtered);
            setGlobalFilter(value);
          } else {
            setFilteredData(searched);
          }
        } else {
          // No active filters - apply search directly
          const searched = filterBySearch(
            data as TreeNode<TData>[],
            value,
            searchConfig.columnsToEnableSearchFor,
            searchConfig.onFilterData as
              | ((d: TreeNode<TData>[], v: string) => TreeNode<TData>[])
              | undefined
          ) as TreeNode<TData>[];
          if (searched === (data as TreeNode<TData>[])) {
            setGlobalFilter(value);
          } else {
            setFilteredData(searched);
          }
        }

        // Track that we just applied this search value
        lastAppliedSearchRef.current = value;

        // Call the onSearch callback
        if (searchConfig.onSearch) {
          searchConfig.onSearch(value);
        }
      }
    },
    [
      data,
      filterConfig?.activeFilters,
      searchConfig.onFilterData,
      searchConfig.columnsToEnableSearchFor,
      searchConfig.onSearch,
      searchConfig.isClientSearchDisabled,
      setGlobalFilter,
      setFilteredData,
    ]
  );

  useEffect(() => {
    if (paginationConfig.type === 'PAGE') {
      table.setPageIndex(0);
    }
  }, [filteredData]);

  // Reset filtered data when original data changes
  useEffect(() => {
    // Only update if the data reference has changed
    if (data !== prevDataRef.current) {
      prevDataRef.current = data as TreeNode<TData>[];

      // Reset filtered data to the new data
      setFilteredData(data as TreeNode<TData>[]);

      // If there's an active search, reapply it to the new data
      if (searchValue.trim() !== '') {
        // Use setTimeout to avoid state update conflicts
        setTimeout(() => {
          handleSearch(searchValue, true);
        }, 0);
      }
    }
  }, [data, handleSearch, searchValue]);

  useEffect(() => {
    const newValue = searchConfig.searchValue ? searchConfig.searchValue : '';
    if (newValue !== searchValue) {
      setSearchValue(newValue);
      handleSearch(newValue, true);
    }
  }, [searchConfig.searchValue]);

  // Apply filters from filterConfig
  useEffect(() => {
    // When manualFiltering is enabled, skip all client-side search filtering
    // The data prop is expected to already be filtered by the parent
    if (searchConfig.isClientSearchDisabled) {
      // Only apply non-search filters if any
      if (filterConfig?.filters && filterConfig.filters.length > 0) {
        const activeFilters = filterConfig.activeFilters || {};
        const hasActiveFilters = Object.keys(activeFilters).some(
          (key) =>
            (Array.isArray(activeFilters[key]) &&
              activeFilters[key].length > 0) ||
            (typeof activeFilters[key] === 'string' &&
              activeFilters[key] !== '')
        );
        if (hasActiveFilters) {
          const filtered = applyFilters(
            data as TreeNode<TData>[],
            filterConfig
          );
          setFilteredData(filtered);
        } else {
          setFilteredData(data as TreeNode<TData>[]);
        }
      } else {
        setFilteredData(data as TreeNode<TData>[]);
      }
      return;
    }

    // If a non-empty search was just applied directly by handleSearch, skip to avoid duplicate work
    // We always want to run when clearing search (empty string) to properly reset state
    if (
      searchValue.trim() !== '' &&
      lastAppliedSearchRef.current === searchValue
    ) {
      // Reset the ref so this check only applies once
      lastAppliedSearchRef.current = '';
      return;
    }

    // If no filterConfig or filters array is empty, just apply search
    if (!filterConfig?.filters || filterConfig.filters.length === 0) {
      if (searchValue.trim() === '') {
        // No filters and no search - reset to original data
        setFilteredData(data as TreeNode<TData>[]);
        setGlobalFilter('');
      } else {
        // Apply search directly to avoid calling handleSearch again
        const searched = filterBySearch(
          data as TreeNode<TData>[],
          searchValue,
          searchConfig.columnsToEnableSearchFor,
          searchConfig.onFilterData as
            | ((d: TreeNode<TData>[], v: string) => TreeNode<TData>[])
            | undefined
        ) as TreeNode<TData>[];
        if (searched === (data as TreeNode<TData>[])) {
          setGlobalFilter(searchValue);
        } else {
          setFilteredData(searched);
        }
      }
      return;
    }

    const activeFilters = filterConfig.activeFilters || {};
    const hasActiveFilters = Object.keys(activeFilters).some(
      (key) =>
        (Array.isArray(activeFilters[key]) && activeFilters[key].length > 0) ||
        (typeof activeFilters[key] === 'string' && activeFilters[key] !== '')
    );

    if (!hasActiveFilters) {
      // No active filters, just apply search if any
      if (searchValue.trim() === '') {
        // No filters and no search - reset to original data
        setFilteredData(data as TreeNode<TData>[]);
        setGlobalFilter('');
      } else {
        // Apply search directly to avoid calling handleSearch again
        const searched = filterBySearch(
          data as TreeNode<TData>[],
          searchValue,
          searchConfig.columnsToEnableSearchFor,
          searchConfig.onFilterData as
            | ((d: TreeNode<TData>[], v: string) => TreeNode<TData>[])
            | undefined
        ) as TreeNode<TData>[];
        if (searched === (data as TreeNode<TData>[])) {
          setGlobalFilter(searchValue);
        } else {
          setFilteredData(searched);
        }
      }
      return;
    }

    // Filter the original data based on active filters
    const filtered = applyFilters(data as TreeNode<TData>[], filterConfig);

    // Now apply search to the filtered data
    if (searchValue.trim() === '') {
      setFilteredData(filtered);
      setGlobalFilter('');
    } else {
      const searched = filterBySearch(
        filtered,
        searchValue,
        searchConfig.columnsToEnableSearchFor,
        searchConfig.onFilterData as
          | ((d: TreeNode<TData>[], v: string) => TreeNode<TData>[])
          | undefined
      ) as TreeNode<TData>[];
      if (searched === filtered) {
        setFilteredData(filtered);
        setGlobalFilter(searchValue);
      } else {
        setFilteredData(searched);
      }
    }
  }, [
    filterConfig?.activeFilters,
    filterConfig?.filters,
    data,
    searchValue,
    searchConfig.onFilterData,
    searchConfig.columnsToEnableSearchFor,
    searchConfig.isClientSearchDisabled,
    setGlobalFilter,
    setFilteredData,
  ]);

  // An empty table caused by a search/filter isn't an unpopulated table, so the
  // caller's "nothing here yet" copy and its add action would be misleading.
  const isSearchActive = searchValue.trim() !== '';
  const areFiltersActive = hasActiveFilters(filterConfig);
  const resolvedEmptyStateConfig: EmptyStateConfigProps =
    (isSearchActive || areFiltersActive) && !emptyStateConfig.renderEmptyState
      ? {
          title: 'No results found',
          description: isSearchActive
            ? `No matches for "${searchValue.trim()}". Try a different search term.`
            : 'Try adjusting your filters.',
        }
      : emptyStateConfig;

  const hasSortableColumns = reorderableGroups.some((group) =>
    group.columns.some((column) => column.sort?.shouldSort)
  );

  const showSortToolbar = sortToolbar && hasSortableColumns;

  const isToolbarVisible =
    !!renderToolbar ||
    columnSelectorConfig.isEnabled ||
    searchConfig.isEnabled ||
    tableViewConfig.isEnabled ||
    showSortToolbar ||
    !!customActions;

  // Position the edge shadows just past the sticky column boundary, so the shadow
  // appears between the pinned columns and the scrolling content. When no sticky
  // group exists on a side, the offset is 0 and the shadow sits at the container edge.
  const leftStickyWidth = getLeftStickyTotalWidth(
    table,
    finalColumnGroups as unknown as ColumnGroup<TreeNode<TData>, TValue>[]
  );
  const rightStickyWidth = getRightStickyTotalWidth(
    table,
    finalColumnGroups as unknown as ColumnGroup<TreeNode<TData>, TValue>[]
  );

  // Sticky scrollbar sync and visibility logic.
  // Uses useLayoutEffect so the initial containerWidth measurement happens
  // synchronously after DOM mutations but BEFORE the browser paints — this
  // prevents a one-frame flicker on mount (e.g. tab switch) where the
  // sticky group-header div briefly auto-sizes to its content before the
  // measured `containerWidth` style kicks in to span the full scroll area.
  useLayoutEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    const tableElement = tableRef.current;
    const container = containerRef.current;

    if (!scrollContainer || !tableElement || !container) return undefined;

    // Check if sticky scrollbar should be shown and update dimensions
    const updateScrollbarState = () => {
      const scrollContainerRect = scrollContainer.getBoundingClientRect();
      // Update container width to trigger re-render for sticky column position recalculation
      setContainerWidth(scrollContainerRect.width);
      // Use table's scrollWidth to get the actual table width including overflow
      const tableWrapperWidth = tableElement.scrollWidth;

      // Show sticky scrollbar if:
      // 1. Table has horizontal overflow
      // 2. The natural scrollbar is below the viewport
      // +1 matches the canScrollRight slop — no flicker at the fill/scroll boundary.
      const hasHorizontalScroll =
        tableWrapperWidth > scrollContainer.clientWidth + 1;
      const scrollbarBelowViewport =
        scrollContainerRect.bottom > window.innerHeight;

      const shouldShow = hasHorizontalScroll && scrollbarBelowViewport;

      setShowStickyScrollbar(shouldShow);

      if (shouldShow) {
        // Set state for sticky scrollbar positioning and width
        setFullTableWidth(tableWrapperWidth);
        setContainerLeft(scrollContainerRect.left);
        setContainerRight(window.innerWidth - scrollContainerRect.right);
      }

      // Edge-shadow visibility: hidden content on either side
      const { scrollLeft, clientWidth, scrollWidth } = scrollContainer;
      setCanScrollLeft(scrollLeft > 0);
      // 1px slop avoids sub-pixel rounding leaving the shadow stuck on
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
    };

    // The scroll container is the actual scrollable element (since we disabled Table's container wrapper)
    const actualScrollElement = scrollContainer;

    let isTableScrolling = false;

    // Sync scroll from table to sticky scrollbar
    const handleTableScroll = () => {
      const stickyScrollbar = stickyScrollbarRef.current;
      if (stickyScrollbar) {
        if (isTableScrolling) return;
        isTableScrolling = true;

        stickyScrollbar.scrollLeft = actualScrollElement.scrollLeft;

        setTimeout(() => {
          isTableScrolling = false;
        }, 0);
      }
    };

    // Check state on mount, data change, and scroll
    updateScrollbarState();

    const resizeObserver = new ResizeObserver(updateScrollbarState);
    resizeObserver.observe(scrollContainer);
    resizeObserver.observe(tableElement);

    window.addEventListener('scroll', updateScrollbarState, true);
    window.addEventListener('resize', updateScrollbarState);
    actualScrollElement.addEventListener('scroll', handleTableScroll);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('scroll', updateScrollbarState, true);
      window.removeEventListener('resize', updateScrollbarState);
      actualScrollElement.removeEventListener('scroll', handleTableScroll);
    };
  }, [filteredData, isLoading]);

  // Separate effect for syncing sticky scrollbar to table
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    const stickyScrollbar = stickyScrollbarRef.current;

    if (!scrollContainer || !stickyScrollbar || !showStickyScrollbar)
      return undefined;

    // The scroll container is the actual scrollable element (since we disabled Table's container wrapper)
    const actualScrollElement = scrollContainer;

    // Initial sync: set sticky scrollbar to match current table scroll position
    stickyScrollbar.scrollLeft = actualScrollElement.scrollLeft;

    let isSyncing = false;

    const handleStickyScroll = () => {
      if (isSyncing) return;
      isSyncing = true;

      actualScrollElement.scrollLeft = stickyScrollbar.scrollLeft;

      setTimeout(() => {
        isSyncing = false;
      }, 0);
    };

    stickyScrollbar.addEventListener('scroll', handleStickyScroll);

    return () => {
      stickyScrollbar.removeEventListener('scroll', handleStickyScroll);
    };
  }, [showStickyScrollbar]);

  // No pinned header logic

  return (
    <div className={cn('w-full flex flex-col', tableWrapperClassName)}>
      {/* No pinned header overlay */}
      {renderToolbar ? (
        <div className={classNames(tableToolbarClassName)}>
          {renderToolbar()}
        </div>
      ) : columnSelectorConfig.isEnabled ||
        searchConfig.isEnabled ||
        tableViewConfig.isEnabled ||
        showSortToolbar ||
        (filterConfig?.filters && filterConfig.filters.length > 0) ? (
        <TableToolbar
          addRowConfig={addRowConfig}
          columnSelectorConfig={columnSelectorConfig}
          searchConfig={searchConfig}
          tableViewConfig={tableViewConfig}
          table={table}
          rowHeightView={rowHeightView}
          setRowHeightView={setRowHeightView}
          handleSearch={handleSearch}
          customActions={customActions}
          groupingConfig={
            groupingConfig as GroupingConfig<TreeNode<TData>> | undefined
          }
          columnGroups={reorderableGroups}
          onApplyColumnGroups={(groups) =>
            setReorderableGroups(
              groups as ColumnGroup<TreeNode<TData>, TValue>[]
            )
          }
          filterConfig={filterConfig}
        />
      ) : customActions ? (
        <div className={classNames(tableToolbarClassName)}>
          {customActions()}
        </div>
      ) : null}
      {summaryConfig && <TableSummary config={summaryConfig} />}
      <div
        className={classNames(
          'relative min-h-0 overflow-clip',
          tableContainerClassName
        )}
        ref={containerRef}
        onMouseEnter={() => setTableHovered(true)}
        onMouseLeave={() => setTableHovered(false)}
      >
        <div className="relative">
          <div
            aria-hidden
            className="pointer-events-none absolute top-0 bottom-0 z-40 w-6 transition-opacity duration-150 ease-out motion-reduce:transition-none"
            style={{
              left: `${leftStickyWidth}px`,
              opacity: canScrollLeft && tableHovered ? 1 : 0,
              background:
                'linear-gradient(to right, rgba(0,0,0,0) 0%, rgba(0,0,0,0.04) 6%, rgba(0,0,0,0.07) 12%, rgba(0,0,0,0.05) 25%, rgba(0,0,0,0.02) 50%, rgba(0,0,0,0.007) 75%, rgba(0,0,0,0) 100%)',
            }}
          />
          <div
            aria-hidden
            className="pointer-events-none absolute top-0 bottom-0 z-40 w-6 transition-opacity duration-150 ease-out motion-reduce:transition-none"
            style={{
              right: `${rightStickyWidth}px`,
              opacity: canScrollRight && tableHovered ? 1 : 0,
              background:
                'linear-gradient(to left, rgba(0,0,0,0) 0%, rgba(0,0,0,0.04) 6%, rgba(0,0,0,0.07) 12%, rgba(0,0,0,0.05) 25%, rgba(0,0,0,0.02) 50%, rgba(0,0,0,0.007) 75%, rgba(0,0,0,0) 100%)',
            }}
          />
          <div
            className="w-full overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            ref={scrollContainerRef}
            // Drives the resize-handle brand color via injected CSS (see
            // ColumnResizeHandle) — reaches body rows that are frozen mid-drag.
            data-dtresizing={isResizingColumn ? 'true' : undefined}
          >
            {!isLoading && errorConfig ? (
              <Table
                ref={tableRef}
                className={classNames(
                  tableClassName,
                  'w-full',
                  groupingConfig?.isEnabled
                    ? ''
                    : 'border-b border-border-primary'
                )}
                disableContainer
              >
                <TBody>
                  <TableError
                    columnsLength={flattenedColumns.length}
                    errorConfig={errorConfig}
                  />
                </TBody>
              </Table>
            ) : !isLoading && !table.getRowModel().rows?.length ? (
              <Table
                ref={tableRef}
                className={classNames(
                  tableClassName,
                  'w-full',
                  groupingConfig?.isEnabled
                    ? ''
                    : 'border-b border-border-primary'
                )}
                disableContainer
              >
                <TBody>
                  <TableEmptyState
                    columnsLength={flattenedColumns.length}
                    emptyStateConfig={resolvedEmptyStateConfig}
                  />
                </TBody>
              </Table>
            ) : (
              <Table
                ref={tableRef}
                className={classNames(
                  tableClassName,
                  'w-full',
                  groupingConfig?.isEnabled
                    ? ''
                    : 'border-b border-border-primary',
                  // Authoritative widths (overrides base table-auto via tw-merge);
                  // runtime sizes + CSS vars stay in `style` below.
                  columnResizeConfig?.isEnabled ? 'table-fixed' : ''
                )}
                disableContainer
                style={
                  columnResizeConfig?.isEnabled
                    ? {
                        // width:100% fills the container so non-sticky columns
                        // absorb the leftover; minWidth = natural total keeps
                        // narrow containers scrolling instead of shrinking columns.
                        width: '100%',
                        minWidth: table.getTotalSize(),
                        ...columnSizeVars,
                      }
                    : undefined
                }
              >
                {!groupingConfig?.isEnabled && (
                  <THead className={classNames(headerClassName)}>
                    <TableHeader
                      groups={finalColumnGroups}
                      table={table}
                      tableHeadClassName={classNames(
                        tableHeadClassName,
                        isToolbarVisible ? '' : ''
                      )}
                      columnResizeConfig={columnResizeConfig}
                      columnResizeControls={columnResizeControls}
                      rowSelectionControl={
                        rowSelectionConfig.isEnabled
                          ? {
                              isEnabled: true,
                              isRowSelected: isRowSelectedFn as (
                                row: TreeNode<TData>
                              ) => boolean,
                              toggleRowById: (id: string) =>
                                toggleRowById(id as string),
                              toggleAll: (subset?: TreeNode<TData>[]) =>
                                toggleAll(
                                  subset as TreeNode<TData>[] | undefined
                                ),
                              getRowId: getRowIdInternal as (
                                row: TreeNode<TData>
                              ) => string,
                            }
                          : undefined
                      }
                    />
                  </THead>
                )}

                <TBody>
                  {isLoading ? (
                    <TableLoadingState
                      groups={finalColumnGroups}
                      columnResizeConfig={columnResizeConfig}
                      rowHeightView={rowHeightView}
                      tableCellClassName={tableCellClassName}
                      tableRowClassName={tableRowClassName}
                      loadingRowsCount={loadingConfig.loadingRowsCount}
                      showPagination={
                        loadingConfig.showPagination &&
                        paginationConfig.isEnabled
                      }
                    />
                  ) : null}

                  {!isLoading && table.getRowModel().rows?.length ? (
                    groupingConfig?.isEnabled ? (
                      // Render grouped rows
                      Array.from(groupedData.entries()).map(
                        ([groupKey, groupRows], groupIndex) => (
                          <TableGroupRow
                            key={groupKey}
                            isFirstGroup={groupIndex === 0}
                            groupKey={groupKey}
                            rows={sortRowsForCurrentSorting(
                              groupRows as TData[]
                            )}
                            isExpanded={expandedGroups[groupKey] ?? false}
                            onToggle={() => toggleGroup(groupKey)}
                            groups={finalColumnGroups}
                            table={table}
                            renderGroupHeader={(grpKey, rows) => {
                              // If toolbar is enabled and has a selected option, use that
                              if (
                                groupingConfig.toolbar?.isEnabled &&
                                groupingConfig.toolbar?.value
                              ) {
                                const selectedOption =
                                  groupingConfig.toolbar.options.find(
                                    (opt) =>
                                      opt.id === groupingConfig.toolbar?.value
                                  );
                                if (selectedOption) {
                                  return selectedOption.renderGroupHeader(
                                    grpKey,
                                    rows as TreeNode<TData>[]
                                  );
                                }
                              }

                              // Otherwise use the main grouping config
                              return groupingConfig.renderGroupHeader(
                                grpKey,
                                rows
                              );
                            }}
                            maxGroupHeight={(() => {
                              // If toolbar is enabled and has a selected option, use that
                              if (
                                groupingConfig.toolbar?.isEnabled &&
                                groupingConfig.toolbar?.value
                              ) {
                                const selectedOption =
                                  groupingConfig.toolbar.options.find(
                                    (opt) =>
                                      opt.id === groupingConfig.toolbar?.value
                                  );
                                if (selectedOption?.maxGroupHeight) {
                                  return selectedOption.maxGroupHeight;
                                }
                              }

                              // Otherwise use the main grouping config
                              return (
                                groupingConfig.maxGroupHeight ??
                                DEFAULT_GROUP_MAX_HEIGHT
                              );
                            })()}
                            borderClassName={(() => {
                              // If toolbar is enabled and has a selected option, use that
                              if (
                                groupingConfig.toolbar?.isEnabled &&
                                groupingConfig.toolbar.value
                              ) {
                                const selectedOption =
                                  groupingConfig.toolbar.options.find(
                                    (opt) =>
                                      opt.id === groupingConfig.toolbar?.value
                                  );
                                if (selectedOption?.groupBorderClassName) {
                                  return selectedOption.groupBorderClassName;
                                }
                              }

                              // Otherwise use the main grouping config
                              return groupingConfig.groupBorderClassName;
                            })()}
                            onRowClick={onRowClick}
                            rowClassName={rowClassName}
                            rowHeightView={rowHeightView}
                            tableCellClassName={tableCellClassName}
                            tableRowClassName={tableRowClassName}
                            tableHeadClassName={tableHeadClassName}
                            containerWidth={containerWidth}
                            rowSelectionControl={
                              rowSelectionConfig.isEnabled
                                ? {
                                    isEnabled: true,
                                    isRowSelected: ((row: TData) =>
                                      (
                                        isRowSelectedFn as unknown as (
                                          r: TData
                                        ) => boolean
                                      )(row)) as (row: TData) => boolean,
                                    toggleRowById: (id: string) =>
                                      toggleRowById(id as string),
                                    toggleAll: (subset?: TData[]) =>
                                      toggleAll(
                                        subset as unknown as
                                          | TreeNode<TData>[]
                                          | undefined
                                      ),
                                    getRowId: ((row: TData) =>
                                      (
                                        getRowIdInternal as unknown as (
                                          r: TData
                                        ) => string
                                      )(row)) as (row: TData) => string,
                                  }
                                : undefined
                            }
                          />
                        )
                      )
                    ) : (
                      <MemoizedTableRow
                        isResizingColumn={isResizingColumn}
                        table={{
                          ...table,
                          getRowModel: () => {
                            const rowModel = table.getRowModel();
                            return {
                              ...rowModel,
                              rows: sortReactTableRowsForCurrentSorting(
                                rowModel.rows as unknown as Parameters<
                                  typeof sortReactTableRowsForCurrentSorting
                                >[0]
                              ) as unknown as typeof rowModel.rows,
                            };
                          },
                        }}
                        groups={finalColumnGroups}
                        // Per-row callbacks are declared over TData; rows reach TableRow as
                        // TreeNode<TData>, which extends it, so widening here is safe.
                        onRowClick={
                          onRowClick as (row: TreeNode<TData>) => void
                        }
                        onRowMouseEnter={
                          onRowMouseEnter as
                            | ((row: TreeNode<TData>) => void)
                            | undefined
                        }
                        onRowMouseLeave={
                          onRowMouseLeave as
                            | ((row: TreeNode<TData>) => void)
                            | undefined
                        }
                        isRowHovered={
                          isRowHovered as
                            | ((row: TreeNode<TData>) => boolean)
                            | undefined
                        }
                        rowContextMenu={
                          rowContextMenu as
                            | ((
                                row: TreeNode<TData>
                              ) => ContextMenuMenuProps | undefined)
                            | undefined
                        }
                        rowDataAttributes={
                          rowDataAttributes as
                            | ((
                                row: TreeNode<TData>
                              ) => Record<string, string> | undefined)
                            | undefined
                        }
                        rowClassName={
                          rowClassName as (row: TreeNode<TData>) => string
                        }
                        stickyRowBgClassName={
                          stickyRowBgClassName as
                            | ((row: TreeNode<TData>) => string | undefined)
                            | undefined
                        }
                        rowHeightView={rowHeightView}
                        tableCellClassName={tableCellClassName}
                        tableRowClassName={tableRowClassName}
                        columnResizeConfig={columnResizeConfig}
                        columnResizeControls={columnResizeControls}
                        rowSelectionControl={
                          rowSelectionConfig.isEnabled
                            ? {
                                isEnabled: true,
                                isRowSelected: ((row: TreeNode<TData>) =>
                                  (
                                    isRowSelectedFn as unknown as (
                                      r: TreeNode<TData>
                                    ) => boolean
                                  )(row)) as (row: TreeNode<TData>) => boolean,
                                toggleRowById: (id: string) =>
                                  toggleRowById(id as string),
                                toggleAll: (subset?: TreeNode<TData>[]) =>
                                  toggleAll(
                                    subset as unknown as
                                      | TreeNode<TData>[]
                                      | undefined
                                  ),
                                getRowId: ((row: TreeNode<TData>) =>
                                  (
                                    getRowIdInternal as unknown as (
                                      r: TreeNode<TData>
                                    ) => string
                                  )(row)) as (row: TreeNode<TData>) => string,
                              }
                            : undefined
                        }
                        expandable={
                          expandableRowConfig?.isEnabled
                            ? {
                                expandedIdSet,
                                toggleExpandById,
                                getRowId: expandableRowConfig.getRowId,
                                renderExpandedContent:
                                  expandableRowConfig.renderExpandedContent,
                                maxExpandedHeight:
                                  expandableRowConfig.maxExpandedHeight ?? 400,
                                borderClassName:
                                  expandableRowConfig.borderClassName,
                                isRowExpandable:
                                  expandableRowConfig.isRowExpandable,
                              }
                            : undefined
                        }
                      />
                    )
                  ) : null}
                </TBody>
              </Table>
            )}
          </div>
        </div>

        {paginationConfig.isEnabled ? (
          <UnifiedTablePagination
            table={table}
            config={paginationConfig}
            onPageSizeChange={handlePageSizeChange}
            currentPageSize={currentPageSize}
          />
        ) : null}
      </div>

      <RowSelectActionMenu
        selectedCount={rowSelectionConfig.selectedCount ?? selectedRows.length}
        renderActionMenu={rowSelectionConfig.rowSelectActionMenu}
        isOpen={
          (rowSelectionConfig.shouldShowRowSelectActionMenu &&
            (rowSelectionConfig.selectedCount ?? selectedRows.length) > 0) ??
          false
        }
        onClose={rowSelectionConfig.onClearSelection ?? unselectAllRows}
      />

      {/* Sticky horizontal scrollbar */}
      {showStickyScrollbar && (
        <div
          ref={stickyScrollbarRef}
          className="fixed z-50 bg-bg-primary border-t border-border-primary"
          style={{
            left: `${containerLeft}px`,
            right: `${containerRight}px`,
            height: '16px',
            overflowX: 'scroll',
            overflowY: 'hidden',
            bottom: '9px',
          }}
        >
          <div
            style={{
              width: `${fullTableWidth}px`,
              height: '1px',
            }}
          />
        </div>
      )}
    </div>
  );
}
